import { useContext, useLayoutEffect, useRef, useState } from "react";
import { BurgerButtonBig, ButtonRound, DropdownInfo, DropdownMenu, HeaderBig, LearnMore, LinkSmart, LoadButton, SignupBox, SquareButton, TextInput, TextInputMultiline } from "../common/Common";
import './Contact.css';
import { LocaleContext, LoginContext, StateContext } from "../common/App";
import { LoadState, Location, Salutation } from '../common/enums';
import { checkEmail, get_translation_objects, postWithCsrf } from "../common/util";
import { Footer } from "./WebsiteCommon";
import { maxContactQueryMessageLength, maxNameLength, maxPhoneNoLength, maxUsernameLength } from "../common/globals_common";
import { validateName, validatePhoneNumber } from "../common/validate";
import { Link } from "react-router-dom";

export function Contact(props) {
    const state = useContext(StateContext);
    const login = useContext(LoginContext);

    const [sendResultText, setSendResultText] = useState(null);
    const [contactQuerySent, setContactQuerySent] = useState(false);

    const [sendState, setSendState] = useState(LoadState.NONE);

    const [location, setLocation] = useState(Location.NUREMBERG);
    const [salutation, setSalutation] = useState(Salutation.NONE);
    const [name, setName] = useState("");
    const [telephoneNo, setTelephoneNo] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState(false);

    const [conditionsAccepted, setConditionsAccepted] = useState(false);

    const locale = useContext(LocaleContext);

    const [locationTransList, locationTransListInverse] = get_translation_objects("location", Location, locale);
    const [salutationTransList, salutationTransListInverse] = get_translation_objects("salutation", Salutation, locale);


    // FUNCTIONS:

    function checkInput() {
        if(!conditionsAccepted ||
                location === Location.NONE ||
                !salutation || salutation === Salutation.NONE ||
                !name || name === "" || name.length > (maxNameLength) ||
                telephoneNo === null || (telephoneNo && telephoneNo.length > maxPhoneNoLength) ||  // Empty is allowed
                !email || email === "" || email.length > maxUsernameLength ||
                !message || message === "" || message.length > maxContactQueryMessageLength || messageError)
        {
            return false;
        }

        return true;
    }

    function handleSend() {
        try {
            let dic = {
                location: location,
                salutation: salutation,
                name: name,
                telephone: telephoneNo,
                email: email,
                message: message
            };

            postWithCsrf("/user/contact_query", JSON.stringify(dic), state).then((response) => {
                if(response.ok) {
                    setSendResultText(<div className="text_success" dangerouslySetInnerHTML={{__html: locale.msgs.CONTACT_QUERY_SUCCESS.replace("${{email}}", email)}}></div>);
                    setContactQuerySent(true);

                    setLocation(Location.NONE);
                    setSalutation("");
                    setTelephoneNo("");
                    setEmail("");
                    setMessage("");
                    setSendState(LoadState.LOAD_SUCCESS);
                    return;
                }

                throw new Error("Failed to send contact query");
            }).catch((error) => {
                setSendState(LoadState.LOAD_ERROR);
                setSendResultText(<div className="text_error">{locale.msgs.CONTACT_QUERY_FAILED}</div>);
            });

            // Calling setSendState() after postWithCsrf() as it could throw an exception
            setSendState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleTelNoClick() {
        window.location.href = "tel:" + locale.msgs.TELEPHONE_NO_NO;
    }


    // RENDER:

    return (
        <div>
            <img className="img_width" src="/customer/header_contact.jpg"></img>

            <div className="text_box">
                <div className="text_small">{locale.msgs.CONTACT}</div>

                <div className="text_big_2 margin_top_middle">{locale.msgs.WE_ARE_THERE_FOR_YOU}</div>
                <div className="text" dangerouslySetInnerHTML={{__html: locale.msgs.WE_ARE_THERE_FOR_YOU_DESCR}}></div>

                { login.loggedIn ?
                    <div className="contact_tel_no_box" onClick={handleTelNoClick}>
                        <img src="/common/icon_phone-colored.svg" />
                        <div className="contact_tel_no">{locale.msgs.TELEPHONE_NO_NO}</div>
                    </div>
                :
                    null
                }
            </div>

            <div className="text_box margin_bottom_high contact_data_box">
                <div className="contact_box">
                    <div className="text_big_2">{locale.msgs.CONTACT}</div>
                    <div className="text">{locale.msgs.QUESTIONS_OR_SUGGESTIONS}</div>

                    { contactQuerySent ? null :
                        <div className="contact_input_element_box">
                            <div className="contact_input_element_first">
                                <DropdownMenu text={locale.msgs.MN_LOCATION} options={Object.values(locationTransList)}
                                        value={locationTransList[location]}
                                        onValueChange={(value) => setLocation(locationTransListInverse[value])}
                                        onScroll={props.onScroll} />
                            </div>
                            
                            <div className="contact_input_element">
                                <DropdownMenu text={locale.msgs.SALUTATION} options={Object.values(salutationTransList)}
                                        value={salutationTransList[salutation]}
                                        onValueChange={(value) => setSalutation(salutationTransListInverse[value])}
                                        onScroll={props.onScroll} />
                            </div>

                            <div className="contact_input_element">
                                <TextInput text={locale.msgs.NAME} value={name}
                                        onChange={(value) => setName(value)}
                                        autocomplete="name"
                                        name="name"
                                        errorText={locale.msgs.PLEASE_ENTER_VALID_NAME}
                                        checker={(value) => validateName(value)}
                                        maxLength={maxNameLength}
                                />
                            </div>

                            <div className="contact_input_element">
                                <TextInput type="tel" text={locale.msgs.TELEPHONE_NO + " (" + locale.msgs.OPTIONAL + ")"} value={telephoneNo}
                                        onChange={(value) => setTelephoneNo(value)}
                                        optional={true}
                                        autocomplete="tel"
                                        name="tel"
                                        errorText={locale.msgs.ENTER_VALID_PHONE_NO}
                                        checker={(value) => validatePhoneNumber(value)}
                                        maxLength={maxPhoneNoLength} />
                            </div>

                            <div className="contact_input_element">
                                <TextInput type="email" text={locale.msgs.EMAIL} value={email}
                                        onChange={(value) => setEmail(value)}
                                        checker={(v) => checkEmail(v)}
                                        autocomplete="email"
                                        name="email"
                                        errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                                        maxLength={maxUsernameLength}
                                />
                            </div>

                            <div className="contact_input_element">
                                <TextInputMultiline text={locale.msgs.MESSAGE} value={message}
                                        onChange={(value) => setMessage(value)}
                                        showPlaceholderIfNotEmpty={true} height="14rem"
                                        maxLength={maxContactQueryMessageLength}
                                        onError={setMessageError}/>
                            </div>

                            <div className="checkbox_box" id="contact_conditions_box">
                                <input type="checkbox" name="conditions" id="conditions_checkbox" className="input_radio_check checkbox"
                                        onChange={(e) => setConditionsAccepted(e.target.checked)} checked={conditionsAccepted}></input>
                                <label className="noselect checkbox_label" htmlFor="conditions_checkbox">

                                    {locale.msgs.ACCEPT_TOS_1}
                                    <LinkSmart className="link_2" to={"/terms_of_use"} target="_blank">{locale.msgs.TOS_2}</LinkSmart>
                                    {locale.msgs.ACCEPT_TOS_2}
                                    <LinkSmart className="link_2" to={"/privacy_policy_contact"} target="_blank">{locale.msgs.PRIVACY_POLICY_CONTACT}</LinkSmart>
                                    {locale.msgs.ACCEPT_TOS_3}

                                </label>
                            </div>

                            <div className="contact_send_button_box">
                                <LoadButton text={locale.msgs.SEND} clickable={checkInput()} onClick={handleSend}
                                        saving={sendState === LoadState.LOADING} />
                            </div>
                        </div>
                    }

                    <div className="send_result_box">
                        {sendResultText}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
